import React, { useMemo, useRef, useEffect, useLayoutEffect, useState, useCallback } from 'react';
import { TimelineItem, ActiveFunctions, DisplayMode, ItemFilters, FunctionType, AnchorMonth } from '@/types/item';
import ItemsFunctionColumn from './ItemsFunctionColumn';
import TimelineBar from './TimelineBar';
import { keyGroupForItem } from '@/lib/keyMilestones';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { cn } from '@/lib/utils';
import { useItemsColumnPermissions, useItemsActionPermissions } from '@/hooks/useItemsPermissions';
import { useUpdateItem } from '@/hooks/useItems';
import { useQueryClient } from '@tanstack/react-query';
import { useIsMobile } from '@/hooks/use-mobile';
import { globalFeatureFlags } from '@/lib/featureFlags';
import { useItemsMilestones, useUpdateMilestone } from '@/hooks/milestones/useItemMilestones';
import { useItemMilestonePermissions } from '@/hooks/milestones/useItemMilestonePermissions';
import { logger } from '@/lib/logger';
import { kpiTracker } from '@/lib/kpiTracking';
import { ChevronRight, ChevronDown, CheckCircle2, AlertCircle, Circle, XCircle, Check, Eye, EyeOff, Camera, Lock, Unlock, ShoppingCart, Tag } from 'lucide-react';
import { useCheckPermission } from '@/hooks/useUserRoles';
import type { ItemOrderStatus } from '@/hooks/procurement/useItemOrderStatus';
import { Button } from '@/components/ui/button';
import MilestoneBar from './milestones/MilestoneBar';
import MilestoneMarker from './milestones/MilestoneMarker';
import { MilestoneErrorBoundary } from './milestones/MilestoneErrorBoundary';
import { toDateKey } from '@/utils/dateTZ';
import { calculateDuration, formatDateDisplay } from '@/utils/timelineCalculations';
import { Badge } from '@/components/ui/badge';
import { useItemCategories } from '@/hooks/useItemCategories';
import { useProjectTeamMembers } from '@/hooks/useProjectTeamMembers';
import { AssigneeBadge } from './AssigneeBadge';
import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import SignOffIndicator from '@/components/projects/signoff/SignOffIndicator';
import { useSignOffSummary } from '@/components/projects/signoff/useSignOffSummary';
import { HIDE_CONTROL_UI } from '@/components/projects/signoff/hideControlUI';
import DependencyDialog from './DependencyDialog';
import { useItemsDependsOn, useMilestonesDependsOn } from '@/hooks/useDependsOn';
import ItemGroupPicker from './ItemGroupPicker';
import { deriveGroupOrder } from './itemGroupOrder';



// Helper function to get local date string
const ymd = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Type-safe column permissions for static override
type ColumnPermissions = {
  project?: boolean;
  item?: boolean;
  category?: boolean;
  shop?: boolean;
  specs?: boolean;
  po?: boolean;
  contract?: boolean;
};

interface ItemsTimelineProps {
  items: TimelineItem[];
  activeFunctions: ActiveFunctions;
  displayMode: DisplayMode;
  anchorDate: string; // ISO date string (YYYY-MM-DD)
  showProjectColumn: boolean;
  showCategoryColumn?: boolean;
  filters: ItemFilters;
  customTimelineRange?: { start: string; end: string };
  onItemClick?: (itemId: string) => void;
  onFunctionClick?: (item: TimelineItem, functionType: FunctionType) => void;
  onMilestoneClick?: (itemId: string, milestoneId: string) => void;
  onCreateParallelItem?: (afterItemId: string, afterSortOrder: number, name: string) => Promise<void> | void;
  onDeleteItem?: (itemId: string) => void;
  hideFunctionsInReport?: boolean;
  hideFunctionsOnMobile?: boolean;
  staticColumnPermissions?: ColumnPermissions; // Static override for print/SSR contexts
  forceShowItemColumn?: boolean; // Nuclear option: force item column visible (print context)
  isReorderMode?: boolean; // Hide milestone expansion when reordering items
  onExpandAll?: () => void;
  onCollapseAll?: () => void;
  expandedItems?: Set<string>;
  onToggleExpand?: (itemId: string) => void;
  staticMilestoneTypes?: any[]; // Static milestone types for print context (no hooks)
  staticMilestonesData?: any[]; // Pre-fetched milestones for print context (feature flag)
  showItemTypeBadges?: boolean; // Show SVC/BLD badges (default: false, only true in Service context)
  // ── PR1: read-only embedding (Site) + photo badges ─────────────────────────
  readOnly?: boolean;
  photoCountByItem?: Map<string, number>;
  photoCountByMilestone?: Map<string, number>;
  onPhotoBadgeClick?: (kind: 'item' | 'milestone', id: string) => void;
  // ───────────────────────────────────────────────────────────────────────────
  summaryMilestoneIds?: Set<string>;  // ✅ NEW - IDs of selected items/milestones for highlighting
  summaryMilestones?: any[];  // ✅ NEW - Resolved summary milestones (with dates) for overlay connector
  summarySelectionMode?: boolean;  // ✅ NEW - Enable milestone selection mode
  onToggleSummarySelection?: (
    id: string,
    type: 'item-start' | 'item-end' | 'milestone' | 'milestone-start' | 'milestone-end',
    data: { itemId: string; itemName: string; date: string; color: string; name?: string; milestoneType?: string }
  ) => void;  // ✅ NEW - Handler for toggle selection
  milestoneFilterRange?: { start: Date; end: Date }; // ✅ NEW - Filter milestones by date range
  stableItemIds?: string[]; // Stable sorted item ids for depends_on queries (avoid refire on sort/paginate)
  itemOrderStatus?: Map<string, ItemOrderStatus>;
  enableZoom?: boolean;
  onSetItemGroup?: (itemId: string, group: string | null) => void;
  showGroupRows?: boolean;
  groupStorageKey?: string;
  expandAllSignal?: number;
  collapseAllSignal?: number;
}

type GroupedRow = { key: string; label: string; items: TimelineItem[]; start: string | null; end: string | null; dur: number; pct: number; };

// Helper to filter milestones based on active filters
const filterMilestones = (
  milestones: any[],
  activeMilestoneTypes?: string[],
  milestoneName?: string,
  dateRange?: { start: Date; end: Date }
): any[] => {
  const hasTypeFilter = activeMilestoneTypes && activeMilestoneTypes.length > 0;
  const hasNameFilter = milestoneName && milestoneName.trim();
  const hasDateFilter = dateRange !== undefined;
  
  // No filter = show all
  if (!hasTypeFilter && !hasNameFilter && !hasDateFilter) return milestones;
  
  return milestones.filter(m => {
    // Type filter
    const typeMatch = !hasTypeFilter || activeMilestoneTypes.includes(m.milestone_type || 'default');
    
    // Name filter
    const nameMatch = !hasNameFilter || (
      m.name && 
      m.name.toLowerCase().includes(milestoneName!.trim().toLowerCase())
    );
    
    // Date range filter - Parse dates in local timezone
    // GUARD: Skip date filter if milestone has NULL/missing dates
    const dateMatch = !hasDateFilter || (() => {
      // If milestone has null/missing dates, include it when no date filter (already covered),
      // but exclude from date-specific filters to avoid crash
      if (!m.start_date || !m.end_date) {
        return false; // Exclude from date range filter results
      }
      
      // Parse milestone dates explicitly as local timezone
      const [mYearS, mMonthS, mDayS] = m.start_date.split('-').map(Number);
      const [mYearE, mMonthE, mDayE] = m.end_date.split('-').map(Number);
      const mStart = new Date(mYearS, mMonthS - 1, mDayS, 0, 0, 0, 0);
      const mEnd = new Date(mYearE, mMonthE - 1, mDayE, 23, 59, 59, 999);
      
      const rStart = new Date(dateRange.start);
      const rEnd = new Date(dateRange.end);
      rStart.setHours(0, 0, 0, 0);
      rEnd.setHours(23, 59, 59, 999);
      
      // Overlap condition
      return mStart <= rEnd && mEnd >= rStart;
    })();
    
    // All conditions must match (AND logic)
    return typeMatch && nameMatch && dateMatch;
  });
};

const ItemsTimeline = ({ 
  items, 
  activeFunctions, 
  displayMode, 
  anchorDate,
  showProjectColumn,
  showCategoryColumn = false,
  filters,
  customTimelineRange,
  onItemClick,
  onFunctionClick,
  onMilestoneClick,
  onCreateParallelItem,
  onDeleteItem,
  hideFunctionsInReport = false,
  hideFunctionsOnMobile = false,
  staticColumnPermissions,
  forceShowItemColumn = false,
  isReorderMode = false,
  onExpandAll,
  onCollapseAll,
  expandedItems: externalExpandedItems,
  onToggleExpand,
  staticMilestoneTypes,
  staticMilestonesData,
  showItemTypeBadges = false,
  readOnly = false,
  photoCountByItem,
  photoCountByMilestone,
  onPhotoBadgeClick,
  summaryMilestoneIds,
  summaryMilestones,
  summarySelectionMode = false,
  onToggleSummarySelection,
  milestoneFilterRange,
  stableItemIds: stableItemIdsProp,
  itemOrderStatus,
  enableZoom,
  onSetItemGroup,
  showGroupRows = false,
  groupStorageKey,
  expandAllSignal,
  collapseAllSignal
}: ItemsTimelineProps) => {
  const timelineRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const headerScrollerRef = useRef<HTMLDivElement>(null);
  const bodyScrollerRef = timelineRef;
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<TimelineItem | null>(null);
  const [internalExpandedItems, setInternalExpandedItems] = useState<Set<string>>(new Set());
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(() => {
    try {
      if (!groupStorageKey) return new Set();
      const saved = localStorage.getItem(groupStorageKey);
      return saved ? new Set(JSON.parse(saved) as string[]) : new Set();
    } catch { return new Set(); }
  });

  const toggleGroup = (k: string) => setCollapsedGroups(p => {
    const n = new Set(p); n.has(k) ? n.delete(k) : n.add(k);
    try { if (groupStorageKey) localStorage.setItem(groupStorageKey, JSON.stringify(Array.from(n))); } catch {}
    return n;
  });

  const groupedRowsRef = useRef<GroupedRow[] | null>(null);

  useEffect(() => {
    if (expandAllSignal === undefined) return;
    setCollapsedGroups(() => {
      const n = new Set<string>();
      try { if (groupStorageKey) localStorage.setItem(groupStorageKey, JSON.stringify([])); } catch {}
      return n;
    });
  }, [expandAllSignal]);

  useEffect(() => {
    if (collapseAllSignal === undefined) return;
    setCollapsedGroups(() => {
      const keys = (groupedRowsRef.current ?? []).map(g => g.key);
      const n = new Set(keys);
      try { if (groupStorageKey) localStorage.setItem(groupStorageKey, JSON.stringify(keys)); } catch {}
      return n;
    });
  }, [collapseAllSignal]);
  
  // Use external state if provided, otherwise use internal state
  const expandedItems = externalExpandedItems ?? internalExpandedItems;
  const setExpandedItems = onToggleExpand ? 
    (updater: Set<string> | ((prev: Set<string>) => Set<string>)) => {
      const newSet = typeof updater === 'function' ? updater(expandedItems) : updater;
      // Call toggle for each item that changed (both expand and collapse)
      const oldIds = Array.from(expandedItems);
      const newIds = Array.from(newSet);
      
      // Find items that changed state
      const changedIds = [
        ...newIds.filter(id => !oldIds.includes(id)),  // Newly expanded
        ...oldIds.filter(id => !newIds.includes(id))   // Newly collapsed
      ];
      
      // Call onToggleExpand for each changed item
      changedIds.forEach(id => onToggleExpand(id));
    } 
    : setInternalExpandedItems;
  
  const dynamicColumnPermissions = useItemsColumnPermissions();
  const { canEditBasic } = useItemsActionPermissions();
  const updateItem = useUpdateItem();
  const queryClient = useQueryClient();
  const handleCommitDates = React.useCallback((id: string, start_date: string, end_date: string) => {
    let prevStart: string | undefined;
    let prevEnd: string | undefined;
    queryClient.setQueriesData({ queryKey: ['items-optimized'] }, (old: any) => {
      if (!Array.isArray(old)) return old;
      return old.map((it: any) => {
        if (it?.id === id) {
          if (prevStart === undefined) {
            prevStart = it.startDate;
            prevEnd = it.endDate;
          }
          return { ...it, startDate: start_date, endDate: end_date };
        }
        return it;
      });
    });
    updateItem.mutate({ id, start_date, end_date } as any, {
      onError: () => {
        if (prevStart !== undefined) {
          queryClient.setQueriesData({ queryKey: ['items-optimized'] }, (old: any) => {
            if (!Array.isArray(old)) return old;
            return old.map((it: any) =>
              it?.id === id ? { ...it, startDate: prevStart, endDate: prevEnd } : it
            );
          });
        }
      },
    });
  }, [updateItem, queryClient]);
  const isMobile = useIsMobile();
  const { data: categoriesData } = useItemCategories();
  
  // ✅ HARDCODED (PR-F1 2026-04-30): ENABLE_SERVICE_ITEM_ASSIGNMENT retired
  
  // Get projectId from first item for team members fetch
  const firstProjectId = items[0]?.projectId || '';

  const groupedRows = useMemo<GroupedRow[] | null>(() => {
    if (!showGroupRows || isReorderMode) return null;
    const map = new Map<string, TimelineItem[]>();
    for (const it of items) {
      const k = ((it as any).group_name as string) || '__none__';
      if (!map.has(k)) map.set(k, []);
      map.get(k)!.push(it);
    }
    const keys = deriveGroupOrder(items).map(g => g.name);
    if (map.has('__none__')) keys.push('__none__');
    // j1: dự án chưa dùng nhóm → không render hàng tiêu đề nào
    if (keys.length === 1 && keys[0] === '__none__') return null;
    return keys.map(k => {
      const list = map.get(k)!;
      const starts = list.map(i => i.startDate).filter(Boolean).sort();
      const ends = list.map(i => i.endDate).filter(Boolean).sort();
      const start = starts[0] ?? null;
      const end = ends[ends.length - 1] ?? null;
      return {
        key: k,
        label: k === '__none__' ? 'Chưa phân nhóm' : k,
        items: list,
        start,
        end,
        dur: (start && end) ? calculateDuration(start, end) : 0,
        pct: (() => {
          let sumW = 0, sumP = 0;
          for (const it of list) {
            const w = (it.startDate && it.endDate) ? calculateDuration(it.startDate, it.endDate) : 1;
            sumW += w; sumP += w * (it.completion_percentage ?? 0);
          }
          return sumW > 0 ? Math.round(sumP / sumW) : 0;
        })(),
      };
    });
  }, [items, showGroupRows, isReorderMode]);
  useEffect(() => { groupedRowsRef.current = groupedRows; }, [groupedRows]);


  const canEditMilestone = useItemMilestonePermissions(firstProjectId).canEdit;
  const updateMilestone = useUpdateMilestone();
  // Sub-PR 3: Planning lock permissions
  const hasItemsLockPlanning = useCheckPermission('ITEMS_LOCK_PLANNING').data || false;
  const hasItemsLockMilestones = useCheckPermission('ITEMS_LOCK_MILESTONES').data || false;
  const canToggleItemLock = !readOnly && canEditBasic && hasItemsLockPlanning;
  const canToggleMilestoneLock = !readOnly && canEditMilestone && hasItemsLockMilestones;
  const handleCommitMilestoneDates = React.useCallback((id: string, itemId: string, start_date: string, end_date: string) => {
    let prevStart: string | undefined;
    let prevEnd: string | undefined;
    queryClient.setQueriesData({ queryKey: ['items-milestones'] }, (old: any) => {
      if (!Array.isArray(old)) return old;
      return old.map((m: any) => {
        if (m?.id !== id) return m;
        if (prevStart === undefined) {
          prevStart = m.start_date;
          prevEnd = m.end_date;
        }
        return { ...m, start_date, end_date };
      });
    });
    queryClient.setQueryData(['item-milestones', itemId], (old: any) => {
      if (!Array.isArray(old)) return old;
      return old.map((m: any) => (m?.id === id ? { ...m, start_date, end_date } : m));
    });
    updateMilestone.mutate({ id, updates: { start_date, end_date } }, {
      onError: () => {
        if (prevStart !== undefined) {
          queryClient.setQueriesData({ queryKey: ['items-milestones'] }, (old: any) => {
            if (!Array.isArray(old)) return old;
            return old.map((m: any) => (m?.id === id ? { ...m, start_date: prevStart, end_date: prevEnd } : m));
          });
          queryClient.setQueryData(['item-milestones', itemId], (old: any) => {
            if (!Array.isArray(old)) return old;
            return old.map((m: any) => (m?.id === id ? { ...m, start_date: prevStart, end_date: prevEnd } : m));
          });
        }
      },
    });
  }, [updateMilestone, queryClient]);
  
  // Sign-off summary for plan module (batch fetch)
  const isSignOffEnabled = !HIDE_CONTROL_UI && globalFeatureFlags.isEnabled('ENABLE_SIGN_OFF_SYSTEM');
  const { data: signOffMap } = useSignOffSummary(firstProjectId, 'plan', isSignOffEnabled && !!firstProjectId);
  
  // Fetch team members for assignee badges
  const { data: teamMembers = [] } = useProjectTeamMembers(firstProjectId, {
    enabled: !!firstProjectId && items.length > 0
  });
  
  // Create category colors map
  const categoryColors = useMemo(() => {
    if (!categoriesData) return {};
    return categoriesData.reduce((acc, cat) => {
      acc[cat.name] = cat.color || '#6b7280';
      return acc;
    }, {} as Record<string, string>);
  }, [categoriesData]);
  
  // ✅ HARDCODED (2025-11-09): Milestone feature always enabled
  const milestoneFeatureEnabled = true;
  const itemIds = items.map(item => item.id);
  
  // Use static data if available (print context), otherwise fetch via hook
  const shouldFetch = milestoneFeatureEnabled && itemIds.length > 0 && !staticMilestonesData;
  const { data: fetchedMilestones = [], isLoading: milestonesLoading } = useItemsMilestones(
    shouldFetch ? itemIds : [],
    { enabled: shouldFetch }
  );

  const milestones = staticMilestonesData || fetchedMilestones;
  
  // Group milestones by item (optimized with shallow comparison)
  const milestonesByItem = useMemo(() => {
    if (!milestoneFeatureEnabled || milestones.length === 0) return {};
    return milestones.reduce((acc, m) => {
      if (!acc[m.item_id]) acc[m.item_id] = [];
      acc[m.item_id].push(m);
      return acc;
  }, {} as Record<string, typeof milestones>);
  }, [milestones, milestoneFeatureEnabled]);

  // Flat milestone list for DependencyDialog (id+name+parent)
  const itemNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const it of items) m.set(it.id, it.name);
    return m;
  }, [items]);
  const flatMilestones = useMemo(() => {
    return milestones
      .filter((m: any) => !!m.id)
      .map((m: any) => ({
        id: m.id as string,
        name: m.name as string,
        parentItemId: m.item_id as string,
        parentItemName: itemNameById.get(m.item_id) || '',
      }));
  }, [milestones, itemNameById]);

  // Dependency dialog state (double-click on bar)
  const [depDialog, setDepDialog] = useState<{ open: boolean; kind: 'item' | 'milestone'; id: string; name: string }>({
    open: false, kind: 'item', id: '', name: '',
  });
  const openItemDependency = useCallback((itemId: string) => {
    const it = items.find(i => i.id === itemId);
    if (!it) return;
    setDepDialog({ open: true, kind: 'item', id: itemId, name: it.name });
  }, [items]);
  const openMilestoneDependency = useCallback((milestoneId: string) => {
    const ms = milestones.find(m => m.id === milestoneId);
    if (!ms) return;
    setDepDialog({ open: true, kind: 'milestone', id: milestoneId, name: ms.name || '' });
  }, [milestones]);

  // Auto-collapse all items when entering reorder mode
  useEffect(() => {
    if (isReorderMode && expandedItems.size > 0) {
      logger.debug('ItemsTimeline', 'Auto-collapsing items in reorder mode', { 
        previousExpandedCount: expandedItems.size 
      });
      setExpandedItems(new Set());
    }
  }, [isReorderMode]);

  // Toggle expand/collapse for item milestones
  const toggleExpand = (itemId: string) => {
    // Disable expand/collapse in reorder mode
    if (isReorderMode) {
      logger.debug('ItemsTimeline', 'Expand disabled in reorder mode');
      return;
    }
    
    setExpandedItems(prev => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
        logger.debug('ItemsTimeline', 'Item collapsed', { itemId });
      } else {
        next.add(itemId);
        logger.debug('ItemsTimeline', 'Item expanded', { itemId, milestonesCount: milestonesByItem[itemId]?.length || 0 });
      }
      return next;
    });
  };

  // Merge dynamic permissions with static override (SSR-safe) + force option
  const isServer = typeof window === 'undefined';
  const columnPermissions = useMemo(() => {
    // Step 1: SSR safety net - auto-enable item column if no static override
    const base = isServer && !staticColumnPermissions
      ? { ...dynamicColumnPermissions, item: true }
      : { ...dynamicColumnPermissions, ...(staticColumnPermissions ?? {}) };
    
    // Step 2: Force override (print context "nuclear option")
    if (forceShowItemColumn) {
      return { ...base, item: true };
    }
    
    return base;
  }, [dynamicColumnPermissions, staticColumnPermissions, isServer, forceShowItemColumn]);

  const [zoomLevel, setZoomLevel] = useState<'day' | 'week' | 'month' | 'fit'>('day');

  // Generate timeline range: Custom range or T to T+6 months based on anchor date
  const { dateColumns, timelineRange } = useMemo(() => {
    let startKey: string, endKey: string;
    
    if (customTimelineRange) {
      // Use custom range when provided (for WeeklyPlan)
      startKey = toDateKey(customTimelineRange.start);
      endKey = toDateKey(customTimelineRange.end);
    } else if (zoomLevel === 'fit') {
      // Fit: dải = kế hoạch thật (item + mốc) + đệm 2 % mỗi đầu (tối thiểu 3 ngày)
      const keys: string[] = [];
      for (const it of items) { try { if (it.startDate) keys.push(toDateKey(it.startDate)); if (it.endDate) keys.push(toDateKey(it.endDate)); } catch {} }
      for (const m of milestones as any[]) { try { if (m?.start_date) keys.push(toDateKey(m.start_date)); if (m?.end_date) keys.push(toDateKey(m.end_date)); } catch {} }
      if (keys.length === 0) {
        startKey = toDateKey(anchorDate);
        const e = new Date(startKey + 'T00:00:00');
        e.setMonth(e.getMonth() + 9);
        endKey = toDateKey(e.toISOString());
      } else {
        keys.sort();
        const minK = keys[0], maxK = keys[keys.length - 1];
        const span = Math.max(1, Math.round((new Date(maxK + 'T00:00:00').getTime() - new Date(minK + 'T00:00:00').getTime()) / 86400000));
        const pad = Math.max(3, Math.ceil(span * 0.02));
        const s = new Date(minK + 'T00:00:00'); s.setDate(s.getDate() - pad);
        const e = new Date(maxK + 'T00:00:00'); e.setDate(e.getDate() + pad);
        startKey = toDateKey(s.toISOString());
        endKey = toDateKey(e.toISOString());
      }
    } else {
      // Default behavior: T to T+6 months from anchor date
      startKey = toDateKey(anchorDate);
      const anchorDateObj = new Date(startKey + 'T00:00:00');
      const endDateObj = new Date(anchorDateObj);
      endDateObj.setMonth(anchorDateObj.getMonth() + 9); // T+9 months
      endKey = toDateKey(endDateObj.toISOString());
      // Tuần/Tháng: dải cố định 9 tháng cắt kế hoạch dài hơn ("tuần thiếu · tháng dư", Sep 26).
      // Nới ĐUÔI tới ngày kết thúc muộn nhất của item/mốc + 7 ngày. Ngày giữ 9 tháng (20 px × 2 năm quá nặng). Fit không qua đây.
      if (zoomLevel === 'week' || zoomLevel === 'month') {
        let maxK = '';
        for (const it of items) { try { if (it.endDate) { const k = toDateKey(it.endDate); if (k > maxK) maxK = k; } } catch {} }
        for (const m of milestones as any[]) { try { if (m?.end_date) { const k = toDateKey(m.end_date); if (k > maxK) maxK = k; } } catch {} }
        if (maxK) { const e2 = new Date(maxK + 'T00:00:00'); e2.setDate(e2.getDate() + 7); const k2 = toDateKey(e2.toISOString()); if (k2 > endKey) endKey = k2; }
      }
    }
    
    // Generate all dates in range using timezone-aware iteration
    const dates = [];
    let curKey = startKey;
    
    while (curKey <= endKey) {
      dates.push(new Date(curKey + 'T00:00:00'));
      // Move to next day
      const nextDate = new Date(curKey + 'T00:00:00');
      nextDate.setDate(nextDate.getDate() + 1);
      curKey = toDateKey(nextDate.toISOString());
    }
    
    const range = {
      start: startKey,
      end: endKey,
      anchorStart: customTimelineRange ? customTimelineRange.start : anchorDate
    };
    
    return { dateColumns: dates, timelineRange: range };
  }, [anchorDate, customTimelineRange, zoomLevel, items, milestones]);

  // Helper function to map function names for display
  const getFunctionDisplayName = (functionName: string): string => {
    if (functionName === 'contract') return 'CTRACT';
    return functionName.toUpperCase();
  };

  // Helper functions
  const isMonthStart = (date: Date) => date.getDate() === 1;
  const isFirstOfMonth = (date: Date) => date.getDate() === 1;
  const isWeekend = (date: Date) => date.getDay() === 0 || date.getDay() === 6;
  const isToday = (date: Date) => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };


  // Sync header horizontal scroll with body scroll
  const handleScroll = () => {
    if (headerScrollerRef.current && bodyScrollerRef.current) {
      headerScrollerRef.current.scrollLeft = bodyScrollerRef.current.scrollLeft;
    }
  };

  // Scroll the timeline body so the bar for the given date range is centered
  const scrollBarToCenter = (startDate: string, endDate: string) => {
    const el = timelineRef.current;
    if (!el) return;

    const sKey = toDateKey(startDate);
    const eKey = toDateKey(endDate);
    let startIndex = dateColumns.findIndex(d => ymd(d) === sKey);
    let endIndex = dateColumns.findIndex(d => ymd(d) === eKey);
    if (startIndex < 0) return;
    if (endIndex < 0) endIndex = startIndex;

    const left = startIndex * DAY_WIDTH;
    const width = (endIndex - startIndex + 1) * DAY_WIDTH;
    const target = left + width / 2 - el.clientWidth / 2;
    const max = Math.max(0, el.scrollWidth - el.clientWidth);
    el.scrollTo({ left: Math.max(0, Math.min(target, max)), behavior: 'smooth' });
  };


  // Performance tracking for milestone rendering
  useEffect(() => {
    if (milestoneFeatureEnabled && milestones.length > 0) {
      const startTime = performance.now();
      
      return () => {
        const renderTime = performance.now() - startTime;
        kpiTracker.recordKPI('ENABLE_CUSTOM_MILESTONES', 'timeline_render_time', renderTime);
        
        logger.debug('ItemsTimeline', 'Render performance', {
          itemsCount: items.length,
          milestonesCount: milestones.length,
          expandedCount: expandedItems.size,
          renderTimeMs: Math.round(renderTime)
        });
      };
    }
  }, [items.length, milestones.length, expandedItems.size, milestoneFeatureEnabled]);

  // Scroll to anchor month when it changes
  useEffect(() => {
    if (timelineRef.current) {
      const anchorDateObj = new Date(anchorDate);
      const anchorIndex = dateColumns.findIndex(date => 
        date.getFullYear() === anchorDateObj.getFullYear() && 
        date.getMonth() === anchorDateObj.getMonth() && 
        date.getDate() === anchorDateObj.getDate()
      );
      
      if (zoomLevel === 'fit') return;
      if (anchorIndex !== -1) {
        const scrollLeft = Math.max(0, anchorIndex * DAY_WIDTH - 200);
        timelineRef.current.scrollLeft = scrollLeft;
      }
    }
  // Chỉ cuộn khi NEO / dải tuỳ chỉnh / nấc zoom đổi. KHÔNG theo dateColumns:
  // từ Sep 26 dateColumns phụ thuộc items+milestones → mọi lần kéo-thả/refetch đều kéo khung về neo (hồi quy).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchorDate, customTimelineRange?.start, customTimelineRange?.end, zoomLevel]);

  const handleDeleteClick = (item: TimelineItem, e: React.MouseEvent) => {
    e.stopPropagation();
    setItemToDelete(item);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (itemToDelete && onDeleteItem) {
      onDeleteItem(itemToDelete.id);
    }
    setDeleteDialogOpen(false);
    setItemToDelete(null);
  };

  // Calculate fixed column widths with permission checks
  const activeFunctionsList = Object.entries(activeFunctions).filter(([_, active]) => active);
  const visibleFunctions = (hideFunctionsInReport || (isMobile && hideFunctionsOnMobile)) ? [] : activeFunctionsList.filter(([functionName]) => {
    if (functionName === 'shop') return columnPermissions.shop;
    if (functionName === 'specs') return columnPermissions.specs;
    if (functionName === 'po') return columnPermissions.po;
    if (functionName === 'contract') return columnPermissions.contract;
    return false;
  });
  
  // Toggle state for mobile column visibility
  const [showMobileColumns, setShowMobileColumns] = useState(false);
  
  // Responsive widths based on screen size
  const PROJECT_WIDTH = isMobile ? 60 : 120;
  const ITEM_WIDTH = isMobile ? 120 : 280;
  const CATEGORY_WIDTH = isMobile ? 40 : 50;
  const DURATION_WIDTH = isMobile && !showMobileColumns ? 0 : (isMobile ? 25 : 35);
  const COMPLETION_WIDTH = isMobile && !showMobileColumns ? 0 : (isMobile ? 30 : 35);
  const START_DATE_WIDTH = isMobile && !showMobileColumns ? 0 : (isMobile ? 55 : 70);
  const END_DATE_WIDTH = isMobile && !showMobileColumns ? 0 : (isMobile ? 55 : 70);
  const FUNCTION_WIDTH = isMobile ? 30 : 40;

  const ganttRef = useRef<HTMLDivElement | null>(null);
  const [ganttW, setGanttW] = useState(0);
  const setGanttNode = useCallback((el: HTMLDivElement | null) => {
    ganttRef.current = el;
    (timelineRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
    if (!el) return;
    const w = el.clientWidth; setGanttW(prev => (prev === w ? prev : w));
  }, []);
  useEffect(() => {
    const el = ganttRef.current; if (!el) return;
    const ro = new ResizeObserver(() => { const w = el.clientWidth; setGanttW(prev => (prev === w ? prev : w)); });
    ro.observe(el); return () => ro.disconnect();
  }, [ganttW === 0]);

  const zoomActive = !!enableZoom && !isMobile;

  const FIT_MIN = 1, FIT_MAX = 20;
  const fitWidth = ganttW > 0 && dateColumns.length > 0 ? Math.max(FIT_MIN, Math.min(FIT_MAX, Math.floor(((ganttW - 2) / dateColumns.length) * 100) / 100)) : FIT_MAX;
  const DAY_WIDTH = isMobile ? 15 : (zoomActive
    ? (zoomLevel === 'fit' ? fitWidth : zoomLevel === 'month' ? 3 : zoomLevel === 'week' ? 7 : 20)
    : 20);
  const labelMode: 'day' | 'week' | 'month' = zoomLevel === 'fit' ? (DAY_WIDTH >= 18 ? 'day' : DAY_WIDTH >= 6 ? 'week' : 'month') : zoomLevel;
  const viewOnly = zoomActive && zoomLevel !== 'day' && !(zoomLevel === 'fit' && DAY_WIDTH >= 12);
  // Zoom buttons: ưu tiên ô header Item; nếu Item ẩn → ô Dur đầu tiên còn hiện; nếu không có ô nào → absolute (fallback).
  const showFixedCols = !isMobile || showMobileColumns;
  const zoomButtons = (
    <>
      <button
        type="button"
        onClick={() => setZoomLevel('day')}
        className={cn(
          "text-[10px] px-1.5 py-0.5 rounded-[4px] border transition-colors",
          zoomLevel === 'day'
            ? "bg-[#0F172A] text-white border-[#0F172A]"
            : "bg-white text-[#475569] border-[#E2E8F0]"
        )}
      >Ngày</button>
      <button
        type="button"
        onClick={() => setZoomLevel('week')}
        className={cn(
          "text-[10px] px-1.5 py-0.5 rounded-[4px] border transition-colors",
          zoomLevel === 'week'
            ? "bg-[#0F172A] text-white border-[#0F172A]"
            : "bg-white text-[#475569] border-[#E2E8F0]"
        )}
      >Tuần</button>
      <button
        type="button"
        onClick={() => setZoomLevel('month')}
        className={cn(
          "text-[10px] px-1.5 py-0.5 rounded-[4px] border transition-colors",
          zoomLevel === 'month'
            ? "bg-[#0F172A] text-white border-[#0F172A]"
            : "bg-white text-[#475569] border-[#E2E8F0]"
        )}
      >Tháng</button>
      <button
        type="button"
        onClick={() => setZoomLevel('fit')}
        title="Co toàn bộ kế hoạch vào bề rộng màn hình (chỉ xem khi kế hoạch dài)"
        className={cn(
          "text-[10px] px-1.5 py-0.5 rounded-[4px] border transition-colors",
          zoomLevel === 'fit'
            ? "bg-[#0F172A] text-white border-[#0F172A]"
            : "bg-white text-[#475569] border-[#E2E8F0]"
        )}
      >Fit</button>
    </>
  );
  const zoomButtonEls = zoomActive ? <div className="flex flex-wrap items-center justify-end gap-1 min-w-0">{zoomButtons}</div> : null;
  useEffect(() => {
    if (zoomLevel !== 'fit') return;
    if (headerScrollerRef.current) headerScrollerRef.current.scrollLeft = 0;
    if (timelineRef.current) timelineRef.current.scrollLeft = 0;
  }, [zoomLevel, DAY_WIDTH]);
  const MILESTONE_ROW_HEIGHT = isMobile ? 24 : 28;
  const MILESTONE_BAR_HEIGHT = isMobile ? 4 : 6;
  
  const leftColumnsWidth = 
    (isMobile ? 40 : 0) + // Toggle button width on mobile
    (showProjectColumn && columnPermissions.project ? PROJECT_WIDTH : 0) + 
    (columnPermissions.item ? ITEM_WIDTH : 0) + 
    (columnPermissions.category && showCategoryColumn ? CATEGORY_WIDTH : 0) + 
    DURATION_WIDTH +
    COMPLETION_WIDTH +
    START_DATE_WIDTH +
    END_DATE_WIDTH +
    (visibleFunctions.length * FUNCTION_WIDTH) +
    (isSignOffEnabled ? 24 : 0);

  // ✅ Overlay connector: draw dependency edges (predecessor.end → successor.start)
  const ITEM_ROW_HEIGHT = isMobile ? 35 : 40;

  // Refs for measuring real row positions
  const containerRef = useRef<HTMLDivElement>(null);
  const rowRefs = useRef<Map<string, HTMLElement>>(new Map());
  const setRowRef = useCallback((key: string) => (el: HTMLElement | null) => {
    if (el) rowRefs.current.set(key, el);
    else rowRefs.current.delete(key);
  }, []);

  // Fetch depends_on for all visible items & their milestones (light, chunked).
  // Use stable sorted ids from parent when provided (ItemsManagement) so the
  // query key doesn't churn on sort/paginate/permission/edit-bar. Fallback to
  // sorted itemIds for call-sites that don't thread the prop (DependencyDialog,
  // print/report). useMilestonesDependsOn filters by item_id `.in(...)`, so the
  // same id set works for both hooks.
  const depIds = useMemo(
    () => stableItemIdsProp ?? itemIds.slice().sort(),
    [stableItemIdsProp, itemIds]
  );
  const { data: itemDepsMap } = useItemsDependsOn(depIds);
  const { data: msDepsMap } = useMilestonesDependsOn(depIds);

  // Step 1: build endpoint map keyed by `${kind}:${id}:${edge}` → { x, rowKey, yOffset, collapsed }
  type Endpoint = { x: number; rowKey: string; yOffset: number; collapsed: boolean };
  const endpoints = useMemo(() => {
    const map = new Map<string, Endpoint>();
    if (dateColumns.length === 0) return map;

    const firstKey = ymd(dateColumns[0]);
    const lastKey = ymd(dateColumns[dateColumns.length - 1]);
    const clampIdx = (dateKey: string): number | null => {
      let idx = dateColumns.findIndex(d => ymd(d) === dateKey);
      if (idx < 0) {
        if (dateKey < firstKey) idx = 0;
        else if (dateKey > lastKey) idx = dateColumns.length - 1;
        else return null;
      }
      return idx;
    };

    // Items: start = startIdx*DAY_WIDTH ; end = (endIdx+1)*DAY_WIDTH
    for (const it of items) {
      if (!it?.id) continue;
      let sKey: string | null = null;
      let eKey: string | null = null;
      try { if (it.startDate) sKey = toDateKey(it.startDate); } catch { /* noop */ }
      try { if (it.endDate) eKey = toDateKey(it.endDate); } catch { /* noop */ }
      const rowKey = `item-${it.id}`;
      if (sKey) {
        const idx = clampIdx(sKey);
        if (idx !== null) map.set(`item:${it.id}:start`, { x: idx * DAY_WIDTH, rowKey, yOffset: -1, collapsed: false });
      }
      if (eKey) {
        const idx = clampIdx(eKey);
        if (idx !== null) map.set(`item:${it.id}:end`, { x: (idx + 1) * DAY_WIDTH, rowKey, yOffset: -1, collapsed: false });
      }
    }

    // Milestones
    for (const [iid, list] of Object.entries(milestonesByItem)) {
      const isExpanded = !!expandedItems?.has(iid);
      for (const m of (list as any[]) || []) {
        if (!m?.id) continue;
        let sKey: string | null = null;
        let eKey: string | null = null;
        try { if (m.start_date) sKey = toDateKey(m.start_date); } catch { /* noop */ }
        try { if (m.end_date) eKey = toDateKey(m.end_date); } catch { /* noop */ }

        if (!isExpanded) {
          // Collapsed: dot point — same coords for start & end
          if (!sKey) continue;
          const idxDot = clampIdx(sKey);
          if (idxDot === null) continue;
          const ep: Endpoint = {
            x: idxDot * DAY_WIDTH + DAY_WIDTH - 6,
            rowKey: `item-${iid}`,
            yOffset: 8,
            collapsed: true,
          };
          map.set(`milestone:${m.id}:start`, ep);
          map.set(`milestone:${m.id}:end`, ep);
        } else {
          const rowKey = `ms-${m.id}`;
          if (sKey) {
            const idx = clampIdx(sKey);
            if (idx !== null) map.set(`milestone:${m.id}:start`, { x: idx * DAY_WIDTH, rowKey, yOffset: -1, collapsed: false });
          }
          if (eKey) {
            const idx = clampIdx(eKey);
            if (idx !== null) map.set(`milestone:${m.id}:end`, { x: (idx + 1) * DAY_WIDTH, rowKey, yOffset: -1, collapsed: false });
          }
        }
      }
    }

    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, milestonesByItem, expandedItems, dateColumns, isMobile, DAY_WIDTH]);

  // Step 2: build edges from depends_on with type + lag (Sub-PR 4.2)
  // Anchor (P): FS/FF → P.end ; SS → P.start  (uses REAL P date)
  // Target (B): targetX = date→px(anchorDate + lag days); targetEdge: FS/SS=start, FF=end
  type DepType = 'FS' | 'SS' | 'FF';
  type Edge = {
    key: string;
    anchorX: number; anchorRowKey: string; anchorYOffset: number;
    targetX: number; targetRowKey: string; targetYOffset: number;
    actualX: number | null;
    type: DepType; lag: number;
  };

  // Predecessor date lookup (real dates, normalized to YYYY-MM-DD)
  const predDates = useMemo(() => {
    const map = new Map<string, { start?: string; end?: string }>();
    for (const it of items) {
      if (!it?.id) continue;
      let s: string | undefined; let e: string | undefined;
      try { if (it.startDate) s = toDateKey(it.startDate); } catch { /* noop */ }
      try { if (it.endDate) e = toDateKey(it.endDate); } catch { /* noop */ }
      map.set(`item:${it.id}`, { start: s, end: e });
    }
    for (const list of Object.values(milestonesByItem)) {
      for (const m of (list as any[]) || []) {
        if (!m?.id) continue;
        let s: string | undefined; let e: string | undefined;
        try { if (m.start_date) s = toDateKey(m.start_date); } catch { /* noop */ }
        try { if (m.end_date) e = toDateKey(m.end_date); } catch { /* noop */ }
        map.set(`milestone:${m.id}`, { start: s, end: e });
      }
    }
    return map;
  }, [items, milestonesByItem]);

  const edges = useMemo<Edge[]>(() => {
    if (dateColumns.length === 0) return [];
    const firstKey = ymd(dateColumns[0]);
    const lastKey = ymd(dateColumns[dateColumns.length - 1]);
    const clampIdx = (dateKey: string): number | null => {
      let idx = dateColumns.findIndex(d => ymd(d) === dateKey);
      if (idx < 0) {
        if (dateKey < firstKey) idx = 0;
        else if (dateKey > lastKey) idx = dateColumns.length - 1;
        else return null;
      }
      return idx;
    };
    const addDays = (dateKey: string, days: number): string => {
      const d = new Date(dateKey + 'T00:00:00');
      d.setDate(d.getDate() + (days | 0));
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${dd}`;
    };

    const out: Edge[] = [];
    const processDeps = (
      bKind: 'item' | 'milestone',
      bId: string,
      deps: { kind: 'item' | 'milestone'; id: string; type?: any; lag?: any }[] | undefined,
    ) => {
      if (!deps || deps.length === 0) return;
      const bEpStart = endpoints.get(`${bKind}:${bId}:start`);
      const bEpEnd = endpoints.get(`${bKind}:${bId}:end`);
      const bEpAny = bEpStart || bEpEnd;
      if (!bEpAny) return;
      for (const dep of deps) {
        if (!dep || (dep.kind !== 'item' && dep.kind !== 'milestone') || !dep.id) continue;
        const type: DepType = dep.type === 'SS' || dep.type === 'FF' ? dep.type : 'FS';
        const lag = typeof dep.lag === 'number' && Number.isFinite(dep.lag) ? dep.lag : 0;
        const pInfo = predDates.get(`${dep.kind}:${dep.id}`);
        if (!pInfo) continue;
        const anchorEdge: 'start' | 'end' = type === 'SS' ? 'start' : 'end';
        const pEp = endpoints.get(`${dep.kind}:${dep.id}:${anchorEdge}`);
        const anchorDate = anchorEdge === 'start' ? pInfo.start : pInfo.end;
        if (!pEp || !anchorDate) continue;
        const targetEdge: 'start' | 'end' = type === 'FF' ? 'end' : 'start';
        // FS: anchor ở mép PHẢI pred (end+1 exclusive) nhưng nối vào START succ (mép trái),
        // nên +1 ngày để waypoint khớp đúng mép cuối bar pred.
        // FF (end→end) & SS (start→start) đã khớp sẵn — KHÔNG +1.
        const lagDays = type === 'FS' ? lag + 1 : lag;
        const computed = addDays(anchorDate, lagDays);
        const tIdx = clampIdx(computed);
        if (tIdx === null) continue;
        const targetX = targetEdge === 'end' ? (tIdx + 1) * DAY_WIDTH : tIdx * DAY_WIDTH;
        const bEp = (targetEdge === 'start' ? bEpStart : bEpEnd) || bEpAny;
        // Actual successor edge (real B date) on same row
        const bInfo = predDates.get(`${bKind}:${bId}`);
        const actualDate = targetEdge === 'end' ? bInfo?.end : bInfo?.start;
        let actualX: number | null = null;
        if (actualDate) {
          const aIdx = clampIdx(actualDate);
          if (aIdx !== null) {
            actualX = targetEdge === 'end' ? (aIdx + 1) * DAY_WIDTH : aIdx * DAY_WIDTH;
          }
        }
        out.push({
          key: `${dep.kind}:${dep.id}->${bKind}:${bId}:${type}:${lag}`,
          anchorX: pEp.x, anchorRowKey: pEp.rowKey, anchorYOffset: pEp.yOffset,
          targetX, targetRowKey: bEp.rowKey, targetYOffset: bEp.yOffset,
          actualX,
          type, lag,
        });
      }
    };

    if (itemDepsMap) {
      for (const it of items) processDeps('item', it.id, itemDepsMap.get(it.id) as any);
    }
    if (msDepsMap) {
      for (const list of Object.values(milestonesByItem)) {
        for (const m of (list as any[]) || []) {
          if (!m?.id) continue;
          processDeps('milestone', m.id, msDepsMap.get(m.id) as any);
        }
      }
    }
    return out;
  }, [endpoints, items, milestonesByItem, itemDepsMap, msDepsMap, predDates, dateColumns, DAY_WIDTH]);

  // Step 3: measure real Y positions via getBoundingClientRect
  const [edgePoints, setEdgePoints] = useState<Array<{
    key: string; p1: { x: number; y: number }; p2: { x: number; y: number };
    actualX: number | null;
    type: DepType; lag: number;
  }>>([]);

  // Stable signature includes ALL computed edge values so any change (date/type/lag) retriggers
  const depSig = useMemo(() => {
    return edges
      .map(e => `${e.key}|${e.anchorX}|${e.targetX}|${e.actualX ?? 'n'}|${e.anchorRowKey}|${e.targetRowKey}|${e.anchorYOffset}|${e.targetYOffset}|${e.type}|${e.lag}`)
      .sort()
      .join(';');
  }, [edges]);

  const expSig = Array.from(expandedItems || []).sort().join('|');
  const itemSig = items.map((i: any) => i.id).join(',');
  const colSig = (dateColumns[0] ? ymd(dateColumns[0]) : '') + ':' + dateColumns.length;

  // Refs to avoid stale closures
  const edgesRef = useRef(edges);
  edgesRef.current = edges;
  const endpointsRef = useRef(endpoints);
  endpointsRef.current = endpoints;

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) {
      setEdgePoints(prev => (prev.length === 0 ? prev : []));
      return;
    }
    const currentEdges = edgesRef.current;
    if (!currentEdges || currentEdges.length === 0) {
      setEdgePoints(prev => (prev.length === 0 ? prev : []));
      return;
    }
    const measure = () => {
      const cont = containerRef.current;
      if (!cont) return;
      const cRect = cont.getBoundingClientRect();
      const eList = edgesRef.current || [];

      const yCache = new Map<string, number | null>();
      const yFor = (rowKey: string, yOffset: number): number | null => {
        const ck = `${rowKey}|${yOffset}`;
        if (yCache.has(ck)) return yCache.get(ck) ?? null;
        const el = rowRefs.current.get(rowKey);
        if (!el) { yCache.set(ck, null); return null; }
        const rect = el.getBoundingClientRect();
        const yo = yOffset === -1 ? rect.height / 2 : yOffset;
        const y = rect.top - cRect.top + yo;
        yCache.set(ck, y);
        return y;
      };

      const pts: Array<{
        key: string; p1: { x: number; y: number }; p2: { x: number; y: number };
        actualX: number | null;
        type: DepType; lag: number;
      }> = [];
      for (const e of eList) {
        const y1 = yFor(e.anchorRowKey, e.anchorYOffset);
        const y2 = yFor(e.targetRowKey, e.targetYOffset);
        if (y1 == null || y2 == null) continue;
        pts.push({
          key: e.key,
          p1: { x: e.anchorX, y: y1 },
          p2: { x: e.targetX, y: y2 },
          actualX: e.actualX,
          type: e.type, lag: e.lag,
        });
      }
      setEdgePoints(prev => {
        if (JSON.stringify(prev) === JSON.stringify(pts)) return prev;
        return pts;
      });
    };
    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    ro?.observe(container);
    window.addEventListener('resize', measure);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', measure);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [depSig, expSig, itemSig, colSig, isMobile]);





  return (
    <ErrorBoundary
      fallback={
        <div className="w-full border rounded-lg overflow-hidden bg-card p-8 text-center">
          <p className="text-destructive font-medium mb-2">Không thể hiển thị timeline</p>
          <p className="text-muted-foreground text-sm mb-4">Đã xảy ra lỗi khi tải dữ liệu</p>
          <button 
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
          >
            Tải lại trang
          </button>
        </div>
      }
    >
    <div className="w-full border rounded-lg overflow-visible bg-card">
      <div className="flex w-full">
        {/* LEFT FIXED SECTION - FROZEN */}
        <div className="shrink-0 bg-background border-r" style={{ width: `${leftColumnsWidth}px` }}>
          {/* Fixed Header */}
          <div ref={headerRef} className="sticky top-0 z-50 flex border-b bg-muted" style={{ height: isMobile ? '50px' : '60px' }}>
            {/* Mobile Column Toggle Button */}
            {isMobile && (
              <div className="px-1 py-2 border-r flex items-center shrink-0" style={{ width: '40px' }}>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowMobileColumns(!showMobileColumns)}
                  className="h-7 w-7 p-0"
                  title={showMobileColumns ? 'Ẩn cột chi tiết' : 'Hiện cột chi tiết'}
                >
                  {showMobileColumns ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            )}
            {Boolean(showProjectColumn && columnPermissions.project) && (
              <div className={`px-1 py-2 border-r font-semibold ${isMobile ? 'text-[10px]' : 'text-xs'} flex items-center`} style={{ width: `${PROJECT_WIDTH}px` }}>
                {isMobile ? 'Proj' : 'Project'}
              </div>
            )}
            {Boolean(columnPermissions.item) && (
              <div
                className={`px-1 py-2 border-r font-semibold ${isMobile ? 'text-[10px]' : 'text-xs'} flex items-center justify-between gap-2 min-w-0`}
                style={{ width: `${ITEM_WIDTH}px` }}
                data-test="item-column-header"
              >
                <span>Item</span>
                {zoomButtonEls}
              </div>
            )}
            {Boolean(columnPermissions.category && showCategoryColumn) && (
              <div className={`px-1 py-2 border-r font-semibold text-[10px] flex items-center`} style={{ width: `${CATEGORY_WIDTH}px` }}>
                Cat
              </div>
            )}
            {(!isMobile || showMobileColumns) && (
              <>
                <div className={`px-1 py-2 border-r font-semibold text-[10px] flex items-center justify-center gap-1`} style={{ width: `${DURATION_WIDTH}px` }}>
                  {zoomButtonEls && !columnPermissions.item ? zoomButtonEls : 'Dur'}
                </div>
                <div className={`px-1 py-2 border-r font-semibold text-[10px] flex items-center justify-center`} style={{ width: `${COMPLETION_WIDTH}px` }}>
                  %
                </div>
                <div className={`px-1 py-2 border-r font-semibold ${isMobile ? 'text-[9px]' : 'text-[10px]'} flex items-center justify-center`} style={{ width: `${START_DATE_WIDTH}px` }}>
                  Start
                </div>
                <div className={`px-1 py-2 border-r font-semibold ${isMobile ? 'text-[9px]' : 'text-[10px]'} flex items-center justify-center`} style={{ width: `${END_DATE_WIDTH}px` }}>
                  End
                </div>
              </>
            )}
            {visibleFunctions.map(([functionName]) => (
               <div 
                 key={functionName}
                 className={`px-1 py-2 border-r font-semibold text-[8px] text-center flex items-center justify-center`}
                 style={{ width: `${FUNCTION_WIDTH}px` }}
                >
                  {isMobile ? functionName.charAt(0).toUpperCase() : getFunctionDisplayName(functionName)}
                </div>
            ))}
            {isSignOffEnabled && (
              <div className="px-0 py-2 font-semibold text-[8px] flex items-center justify-center"
                style={{ width: '24px' }}>
              </div>
            )}
          </div>
          
          {/* Fixed Body Columns */}
          <div className="flex flex-col">
            {(groupedRows ?? [{ key: '__all__', label: '', items, start: null, end: null }]).map((g) => (
              <React.Fragment key={g.key}>
                {groupedRows && (
                  <div className="flex border-b bg-muted/40" style={{ height: isMobile ? '35px' : '40px' }}>
                    {isMobile && (
                      <div className="border-r" style={{ width: '40px', height: '100%' }} />
                    )}
                    {Boolean(showProjectColumn && columnPermissions.project) && (
                      <div className="px-1 py-2 border-r" style={{ width: `${PROJECT_WIDTH}px`, height: '100%' }} />
                    )}
                    {Boolean(columnPermissions.item) && (
                      <div className="px-1 py-2 border-r flex items-center gap-1.5" style={{ width: `${ITEM_WIDTH}px`, height: '100%', paddingLeft: 4 }}>
                        <span className="font-semibold text-sm truncate">{g.label}</span>
                        <span className="text-[10px] text-muted-foreground shrink-0">{g.items.length} việc</span>
                        <button type="button" className="shrink-0 p-0.5 hover:bg-muted rounded ml-auto" onClick={() => toggleGroup(g.key)}>
                          {collapsedGroups.has(g.key)
                            ? <ChevronRight className="h-4 w-4" />
                            : <ChevronDown className="h-4 w-4" />}
                        </button>
                      </div>
                    )}
                    {Boolean(columnPermissions.category && showCategoryColumn) && (
                      <div className="px-1 py-2 border-r" style={{ width: `${CATEGORY_WIDTH}px`, height: '100%' }} />
                    )}
                    {(!isMobile || showMobileColumns) && (
                      <>
                        <div className="px-1 py-2 border-r text-[10px] text-muted-foreground flex items-center justify-center" style={{ width: `${DURATION_WIDTH}px`, height: '100%' }}>
                          {g.dur ? `${g.dur}d` : '—'}
                        </div>
                        <div className={`px-1 py-2 border-r ${isMobile ? 'text-[8px]' : 'text-[10px]'} font-medium flex items-center justify-center`} style={{ width: `${COMPLETION_WIDTH}px`, height: '100%' }}>
                          {g.pct ? `${g.pct}%` : '—'}
                        </div>
                        <div className="px-1 py-2 border-r text-[10px] text-muted-foreground flex items-center justify-center" style={{ width: `${START_DATE_WIDTH}px`, height: '100%' }}>
                          {g.start ? formatDateDisplay(g.start, 'short') : '—'}
                        </div>
                        <div className="px-1 py-2 border-r text-[10px] text-muted-foreground flex items-center justify-center" style={{ width: `${END_DATE_WIDTH}px`, height: '100%' }}>
                          {g.end ? formatDateDisplay(g.end, 'short') : '—'}
                        </div>
                      </>
                    )}
                    {visibleFunctions.map(([functionName]) => (
                      <div key={functionName} className="border-r" style={{ width: `${FUNCTION_WIDTH}px`, height: '100%' }} />
                    ))}
                    {isSignOffEnabled && (
                      <div className="border-r" style={{ width: '24px', height: '100%' }} />
                    )}
                  </div>
                )}
                {(collapsedGroups.has(g.key) ? [] : g.items).map((item, itemIndex) => {
              const allMilestones = milestonesByItem[item.id] || [];
              const itemMilestones = filterMilestones(allMilestones, filters.activeMilestoneTypes, filters.milestoneName, milestoneFilterRange);
              const isExpanded = expandedItems.has(item.id);
              const hasHighlight = item.hasHighlightedMilestone;
              const isStartSelected = summaryMilestoneIds?.has(`item-start-${item.id}`);
              const isEndSelected = summaryMilestoneIds?.has(`item-end-${item.id}`);
              
              return (
                <React.Fragment key={item.id}>
                  {/* Main Item Row */}
                  <div className={cn("flex border-b", 
                    hasHighlight && "bg-primary/5",
                    (isStartSelected || isEndSelected) && "bg-green-50 dark:bg-green-950/20 border-l-4 border-l-green-500",
                    summarySelectionMode && "hover:bg-muted/50 cursor-pointer"
                  )} style={{ height: isMobile ? '35px' : '40px' }}>
                    {Boolean(showProjectColumn && columnPermissions.project) && (
                      <div className={`px-1 py-2 border-r ${isMobile ? 'text-[10px]' : 'text-xs'} flex items-center truncate`} style={{ width: `${PROJECT_WIDTH}px`, height: '100%' }}>
                        {isMobile ? item.projectName?.substring(0, 8) : item.projectName}
                      </div>
                    )}
                    {Boolean(columnPermissions.item) && (
                      <div 
                        className={cn(
                          "px-1 py-2 border-r flex items-center gap-1",
                          !summarySelectionMode && "cursor-pointer hover:bg-muted/30"
                        )}
                        style={{ width: `${ITEM_WIDTH}px`, height: '100%', paddingLeft: groupedRows ? 14 : undefined }}
                        onClick={() => {
                          if (summarySelectionMode) {
                            // Toggle selection instead of opening edit dialog
                            onToggleSummarySelection?.(
                              `item-start-${item.id}`,
                              'item-start',
                              {
                                itemId: item.id,
                                itemName: item.name,
                                date: item.startDate,
                                color: categoryColors[item.category] || '#6b7280'
                              }
                            );
                          } else {
                            scrollBarToCenter(item.startDate, item.endDate);
                          }
                        }}
                        onDoubleClick={() => {
                          if (!summarySelectionMode) onItemClick?.(item.id);
                        }}

                      >
                        {summarySelectionMode && (
                          <div className={cn(
                            "w-4 h-4 border-2 rounded flex items-center justify-center shrink-0",
                            isStartSelected 
                              ? "bg-green-500 border-green-500" 
                              : "border-muted-foreground"
                          )}>
                            {isStartSelected && (
                              <Check className="h-3 w-3 text-white" />
                            )}
                          </div>
                        )}
                        {!isReorderMode && milestoneFeatureEnabled && itemMilestones.length > 0 && (
                          <button
                            className="shrink-0 p-0.5 hover:bg-muted rounded"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleExpand(item.id);
                            }}
                          >
                            {isExpanded ? (
                              <ChevronDown className={isMobile ? "h-3 w-3" : "h-4 w-4"} />
                            ) : (
                              <ChevronRight className={isMobile ? "h-3 w-3" : "h-4 w-4"} />
                            )}
                          </button>
                        )}
                        <div className={`font-medium ${isMobile ? 'text-[10px]' : 'text-xs'} truncate flex items-center gap-1.5`} title={item.name}>
                          <span className="truncate">
                            {isMobile && item.name.length > 15 ? item.name.substring(0, 15) + '...' : item.name}
                          </span>
                          {(() => {
                            const st = itemOrderStatus?.get(item.id);
                            if (!st) return null;
                            const colorClass = st.level === 3 ? 'text-fuchsia-400' : st.level === 2 ? 'text-sky-500' : 'text-slate-400';
                            const title = st.level === 3 ? 'Đã chi' : st.level === 2 ? 'Đã có nhà thầu' : 'Đã tạo đơn';
                            return (
                              <span className={cn("inline-flex items-center gap-0.5 text-[10px] shrink-0 pointer-events-none", colorClass)} title={title}>
                                <ShoppingCart className="h-3 w-3 shrink-0" />
                                <span>{st.count}/{st.total}</span>
                              </span>
                            );
                          })()}
                          {onSetItemGroup && !readOnly && (
                            <span className="shrink-0" onClick={(e) => e.stopPropagation()}
                                  onDoubleClick={(e) => e.stopPropagation()}>
                              <ItemGroupPicker
                                projectId={item.projectId || ''}
                                value={(item as any).group_name ?? null}
                                onChange={(v) => onSetItemGroup(item.id, v)}
                                disabled={!canEditBasic}
                              />
                            </span>
                          )}
                          {showItemTypeBadges && item.itemType && (
                            <Badge 
                              variant={item.itemType === 'service' ? 'secondary' : 'default'}
                              className="text-[8px] px-1 py-0 shrink-0"
                            >
                              {item.itemType === 'service' ? 'SVC' : 'BLD'}
                            </Badge>
                          )}
                          {item.assigned_user_id && (
                            <AssigneeBadge 
                              assignedUserId={item.assigned_user_id}
                              teamMembers={teamMembers}
                            />
                          )}
                          {photoCountByItem?.get(item.id) ? (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); onPhotoBadgeClick?.('item', item.id); }}
                              className="shrink-0 inline-flex items-center gap-0.5 px-1 py-0 rounded bg-muted hover:bg-accent text-[9px] leading-none"
                              title={`${photoCountByItem.get(item.id)} ảnh`}
                            >
                              <Camera className="h-2.5 w-2.5" />
                              {photoCountByItem.get(item.id)}
                            </button>
                          ) : null}
                          {/* Sub-PR 3: Planning lock (item) */}
                          {canToggleItemLock ? (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                updateItem.mutate({ id: item.id, planning_locked: !item.planning_locked } as any);
                              }}
                              className="shrink-0 inline-flex items-center px-0.5 py-0 rounded hover:bg-accent"
                              title={item.planning_locked ? 'Mở khoá kế hoạch' : 'Khoá kế hoạch'}
                              aria-label={item.planning_locked ? 'Mở khoá kế hoạch' : 'Khoá kế hoạch'}
                            >
                              {item.planning_locked ? (
                                <Lock className="h-3 w-3 text-orange-500" />
                              ) : (
                                <Unlock className="h-3 w-3 text-muted-foreground" />
                              )}
                            </button>
                          ) : item.planning_locked ? (
                            <Lock className="h-3 w-3 text-orange-500 shrink-0" aria-label="Đã khoá kế hoạch" />
                          ) : null}
                        </div>
                      </div>
                    )}
                    {Boolean(columnPermissions.category && showCategoryColumn) && (
                      <div className={`px-1 py-2 border-r text-[10px] text-muted-foreground flex items-center`} style={{ width: `${CATEGORY_WIDTH}px`, height: '100%' }}>
                        {item.category}
                      </div>
                    )}
                    {(!isMobile || showMobileColumns) && (
                      <>
                        <div className={`px-1 py-2 border-r text-[10px] text-muted-foreground flex items-center justify-center`} style={{ width: `${DURATION_WIDTH}px`, height: '100%' }}>
                          {calculateDuration(item.startDate, item.endDate)}d
                        </div>
                        <div className={`px-1 py-2 border-r ${isMobile ? 'text-[8px]' : 'text-[10px]'} font-medium flex items-center justify-center`} style={{ width: `${COMPLETION_WIDTH}px`, height: '100%' }}>
                          {(() => {
                            const completion = item.completion_percentage ?? 0;
                            const isManual = item.manual_completion_percentage !== null && 
                                             item.manual_completion_percentage !== undefined;
                            
                            if (completion === 0) return '%';
                            
                            return (
                              <span className={isManual ? 'text-orange-600' : ''} title={isManual ? 'Thủ công' : ''}>
                                {completion}%
                              </span>
                            );
                          })()}
                        </div>
                        <div 
                          className={cn(
                            "px-1 py-2 border-r text-[10px] text-muted-foreground flex items-center justify-center",
                            summarySelectionMode && "cursor-pointer hover:bg-muted/50",
                            isStartSelected && "bg-green-50 border-green-500"
                          )}
                          style={{ width: `${START_DATE_WIDTH}px`, height: '100%' }}
                          onClick={() => {
                            if (summarySelectionMode) {
                              onToggleSummarySelection?.(
                                `item-start-${item.id}`,
                                'item-start',
                                {
                                  itemId: item.id,
                                  itemName: item.name,
                                  date: item.startDate,
                                  color: categoryColors[item.category] || '#6b7280'
                                }
                              );
                            }
                          }}
                        >
                          {summarySelectionMode && (
                            <div className={cn("w-3 h-3 border rounded mr-1", 
                              isStartSelected ? "bg-green-500" : "border-muted-foreground"
                            )}>
                              {isStartSelected && <Check className="h-2 w-2 text-white" />}
                            </div>
                          )}
                          {formatDateDisplay(item.startDate, 'short')}
                        </div>
                        <div 
                          className={cn(
                            "px-1 py-2 border-r text-[10px] text-muted-foreground flex items-center justify-center",
                            summarySelectionMode && "cursor-pointer hover:bg-muted/50",
                            isEndSelected && "bg-green-50 border-green-500"
                          )}
                          style={{ width: `${END_DATE_WIDTH}px`, height: '100%' }}
                          onClick={() => {
                            if (summarySelectionMode) {
                              onToggleSummarySelection?.(
                                `item-end-${item.id}`,
                                'item-end',
                                {
                                  itemId: item.id,
                                  itemName: item.name,
                                  date: item.endDate,
                                  color: categoryColors[item.category] || '#6b7280'
                                }
                              );
                            }
                          }}
                        >
                          {summarySelectionMode && (
                            <div className={cn("w-3 h-3 border rounded mr-1", 
                              isEndSelected ? "bg-green-500" : "border-muted-foreground"
                            )}>
                              {isEndSelected && <Check className="h-2 w-2 text-white" />}
                            </div>
                          )}
                          {formatDateDisplay(item.endDate, 'short')}
                        </div>
                      </>
                    )}
                    {visibleFunctions.map(([functionName]) => (
                      <div key={functionName} className="border-r flex items-center" style={{ width: `${FUNCTION_WIDTH}px`, height: '100%' }}>
                        <ItemsFunctionColumn
                          item={item}
                          functionType={functionName as FunctionType}
                          activeFunctions={activeFunctions}
                          activeGates={filters.activeGates}
                          onClick={() => onFunctionClick?.(item, functionName as FunctionType)}
                        />
                      </div>
                    ))}
                    {isSignOffEnabled && (
                      <div className="flex items-center justify-center" style={{ width: '24px', height: '100%' }}>
                        <SignOffIndicator
                          projectId={firstProjectId}
                          module="plan"
                          entityType="plan_item"
                          entityId={item.id}
                          entityLabel={item.name}
                          signOffData={signOffMap?.get(item.id)}
                        />
                      </div>
                    )}
                  </div>
                  {!isReorderMode && milestoneFeatureEnabled && isExpanded && itemMilestones.map((milestone) => {
                    const isHighlighted = filters.activeMilestoneTypes?.includes(milestone.milestone_type);
                    const getStatusIcon = (status: string) => {
                      switch (status) {
                        case 'completed':
                          return <CheckCircle2 className="h-3 w-3 text-green-500" />;
                        case 'in_progress':
                          return <Circle className="h-3 w-3 text-blue-500" />;
                        case 'blocked':
                          return <XCircle className="h-3 w-3 text-red-500" />;
                        case 'pending':
                          return <AlertCircle className="h-3 w-3 text-yellow-500" />;
                        default:
                          return <Circle className="h-3 w-3 text-muted-foreground" />;
                      }
                    };
                    
                    const milestoneType = staticMilestoneTypes?.find(mt => mt.name === milestone.milestone_type);
                    const milestoneColor = milestoneType?.color || '#6b7280';
                    const isMsStartSelected = summaryMilestoneIds?.has(`milestone-start-${milestone.id}`) ?? false;
                    const isMsEndSelected = summaryMilestoneIds?.has(`milestone-end-${milestone.id}`) ?? false;
                    const isMsAnySelected = isMsStartSelected || isMsEndSelected || (summaryMilestoneIds?.has(milestone.id) ?? false);
                    return (
                      <div
                        key={milestone.id}
                        className={cn(
                          "flex border-b",
                          isHighlighted ? "bg-primary/10" : "bg-muted/20",
                          isMsAnySelected && "bg-blue-50 dark:bg-blue-950/20 border-l-4 border-l-blue-500"
                        )}
                        style={{ height: `${MILESTONE_ROW_HEIGHT}px` }}
                      >
                        {/* Empty project column */}
                        {Boolean(showProjectColumn && columnPermissions.project) && (
                          <div className="border-r" style={{ width: `${PROJECT_WIDTH}px` }} />
                        )}
                        
                        {/* Milestone name with indent, status icon, and selection checkbox (shortcut → milestone-start) */}
                        {Boolean(columnPermissions.item) && (
                          <div 
                            className={cn(
                              `px-1 py-1 border-r flex items-center gap-1 ${isMobile ? 'text-[9px]' : 'text-xs'} text-muted-foreground`,
                              summarySelectionMode && "cursor-pointer hover:bg-blue-100"
                            )}
                            style={{ width: `${ITEM_WIDTH}px`, paddingLeft: summarySelectionMode ? '28px' : '40px' }}
                            title={milestone.name}
                            onClick={() => {
                              if (summarySelectionMode && onToggleSummarySelection) {
                                onToggleSummarySelection(
                                  `milestone-start-${milestone.id}`,
                                  'milestone-start',
                                  {
                                    itemId: milestone.item_id,
                                    itemName: item.name,
                                    date: milestone.start_date,
                                    color: milestoneColor,
                                    name: milestone.name,
                                    milestoneType: milestone.milestone_type
                                  }
                                );
                              } else if (!summarySelectionMode) {
                                scrollBarToCenter(milestone.start_date, milestone.end_date || milestone.start_date);
                              }
                            }}
                            onDoubleClick={() => {
                              if (!summarySelectionMode) onMilestoneClick?.(milestone.item_id, milestone.id);
                            }}

                          >
                            {summarySelectionMode && (
                              <div className={cn(
                                "w-3 h-3 border-2 rounded flex items-center justify-center shrink-0",
                                isMsStartSelected
                                  ? "bg-blue-500 border-blue-500" 
                                  : "border-muted-foreground"
                              )}>
                                {isMsStartSelected && (
                                  <Check className="h-2 w-2 text-white" />
                                )}
                              </div>
                            )}
                            {getStatusIcon(milestone.status)}
                            <span className="truncate">{milestone.name}</span>
                            {milestone.assigned_user_id && (
                              <AssigneeBadge 
                                assignedUserId={milestone.assigned_user_id}
                                teamMembers={teamMembers}
                              />
                            )}
                            {photoCountByMilestone?.get(milestone.id!) ? (
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); onPhotoBadgeClick?.('milestone', milestone.id!); }}
                                className="shrink-0 inline-flex items-center gap-0.5 px-1 py-0 rounded bg-muted hover:bg-accent text-[9px] leading-none"
                                title={`${photoCountByMilestone.get(milestone.id!)} ảnh`}
                              >
                                <Camera className="h-2.5 w-2.5" />
                                {photoCountByMilestone.get(milestone.id!)}
                              </button>
                            ) : null}
                            {/* Sub-PR 3: Planning lock (milestone) */}
                            {canToggleMilestoneLock ? (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  updateMilestone.mutate({ id: milestone.id!, updates: { planning_locked: !(milestone as any).planning_locked } });
                                }}
                                className="shrink-0 inline-flex items-center px-0.5 py-0 rounded hover:bg-accent"
                                title={(milestone as any).planning_locked ? 'Mở khoá milestone' : 'Khoá milestone'}
                                aria-label={(milestone as any).planning_locked ? 'Mở khoá milestone' : 'Khoá milestone'}
                              >
                                {(milestone as any).planning_locked ? (
                                  <Lock className="h-3 w-3 text-orange-500" />
                                ) : (
                                  <Unlock className="h-3 w-3 text-muted-foreground" />
                                )}
                              </button>
                            ) : (milestone as any).planning_locked ? (
                              <Lock className="h-3 w-3 text-orange-500 shrink-0" aria-label="Đã khoá milestone" />
                            ) : null}
                          </div>
                        )}
                        
                      {/* Empty category column */}
                      {Boolean(columnPermissions.category && showCategoryColumn) && (
                        <div className="border-r" style={{ width: `${CATEGORY_WIDTH}px` }} />
                      )}
                       
                       {/* Duration, %, Start, End columns - hidden on mobile unless toggled */}
                      {(!isMobile || showMobileColumns) && (
                        <>
                          <div className={`px-1 py-2 border-r text-[10px] text-muted-foreground flex items-center justify-center`} style={{ width: `${DURATION_WIDTH}px` }}>
                            {calculateDuration(milestone.start_date, milestone.end_date)}d
                          </div>
                          <div className={`px-1 py-2 border-r ${isMobile ? 'text-[8px]' : 'text-[10px]'} font-medium flex items-center justify-center`} style={{ width: `${COMPLETION_WIDTH}px` }}>
                            {(() => {
                              const completion = milestone.completion_percentage ?? 0;
                              return completion === 0 ? '%' : `${completion}%`;
                            })()}
                          </div>
                          <div
                            className={cn(
                              `px-1 py-2 border-r ${isMobile ? 'text-[8px]' : 'text-[10px]'} text-muted-foreground flex items-center justify-center`,
                              summarySelectionMode && "cursor-pointer hover:bg-muted/50",
                              isMsStartSelected && "bg-green-50 border-green-500"
                            )}
                            style={{ width: `${START_DATE_WIDTH}px` }}
                            onClick={() => {
                              if (summarySelectionMode) {
                                onToggleSummarySelection?.(
                                  `milestone-start-${milestone.id}`,
                                  'milestone-start',
                                  {
                                    itemId: milestone.item_id,
                                    itemName: item.name,
                                    date: milestone.start_date,
                                    color: milestoneColor,
                                    name: milestone.name,
                                    milestoneType: milestone.milestone_type
                                  }
                                );
                              } else {
                                scrollBarToCenter(milestone.start_date, milestone.end_date || milestone.start_date);
                              }
                            }}
                            onDoubleClick={() => {
                              if (!summarySelectionMode) onMilestoneClick?.(milestone.item_id, milestone.id);
                            }}

                          >
                            {summarySelectionMode && (
                              <div className={cn("w-3 h-3 border rounded mr-1 flex items-center justify-center",
                                isMsStartSelected ? "bg-green-500" : "border-muted-foreground"
                              )}>
                                {isMsStartSelected && <Check className="h-2 w-2 text-white" />}
                              </div>
                            )}
                            {formatDateDisplay(milestone.start_date, 'short')}
                          </div>
                          <div
                            className={cn(
                              `px-1 py-2 border-r ${isMobile ? 'text-[8px]' : 'text-[10px]'} text-muted-foreground flex items-center justify-center`,
                              summarySelectionMode && "cursor-pointer hover:bg-muted/50",
                              isMsEndSelected && "bg-green-50 border-green-500"
                            )}
                            style={{ width: `${END_DATE_WIDTH}px` }}
                            onClick={() => {
                              if (summarySelectionMode) {
                                onToggleSummarySelection?.(
                                  `milestone-end-${milestone.id}`,
                                  'milestone-end',
                                  {
                                    itemId: milestone.item_id,
                                    itemName: item.name,
                                    date: milestone.end_date,
                                    color: milestoneColor,
                                    name: milestone.name,
                                    milestoneType: milestone.milestone_type
                                  }
                                );
                              } else {
                                scrollBarToCenter(milestone.start_date, milestone.end_date || milestone.start_date);
                              }
                            }}
                            onDoubleClick={() => {
                              if (!summarySelectionMode) onMilestoneClick?.(milestone.item_id, milestone.id);
                            }}

                          >
                            {summarySelectionMode && (
                              <div className={cn("w-3 h-3 border rounded mr-1 flex items-center justify-center",
                                isMsEndSelected ? "bg-green-500" : "border-muted-foreground"
                              )}>
                                {isMsEndSelected && <Check className="h-2 w-2 text-white" />}
                              </div>
                            )}
                            {formatDateDisplay(milestone.end_date, 'short')}
                          </div>
                        </>
                      )}
                        {visibleFunctions.map(([functionName]) => (
                          <div key={functionName} className="border-r" style={{ width: `${FUNCTION_WIDTH}px` }} />
                        ))}
                      </div>
                    );
                  })}
                </React.Fragment>
              );
            })}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* RIGHT TIMELINE SECTION - header sticky, body scrolls horizontally */}
        <div className="flex-1 min-w-0 relative">
          {zoomActive && !columnPermissions.item && !showFixedCols && (
            <div className="absolute right-2 top-1 z-[60] flex items-center gap-1">
              {zoomButtons}
            </div>
          )}
          {/* Header scroller - sticky to page, synced horizontally with body */}
          <div
            ref={headerScrollerRef}
            className="sticky top-0 z-50 bg-background border-b"
            style={{ overflowX: 'hidden', height: isMobile ? '50px' : '60px' }}
          >
            <div style={{ width: `${dateColumns.length * DAY_WIDTH}px`, height: '100%' }}>
              <div className="relative bg-muted/50 h-full">
                {dateColumns.map((date, index) => (
                  <div
                    key={index}
                    className={cn(
                      "absolute top-0 h-full py-1 text-xs text-center flex items-center justify-center",
                      DAY_WIDTH >= 4 && "border-r",
                      !zoomActive || labelMode === 'day' ? "px-0.5" : "px-0",
                      isWeekend(date) && "bg-muted",
                      isMonthStart(date) && (
                        zoomActive && labelMode !== 'day'
                          ? "border-l border-primary/40 bg-primary/5"
                          : "border-l-2 border-primary bg-primary/10"
                      ),

                      isToday(date) && "bg-primary text-primary-foreground font-bold",
                      zoomActive && (
                        (labelMode === 'week' && date.getDay() !== 1) ||
                        (labelMode === 'month' && date.getDate() !== 1)
                      ) && "border-r-transparent"
                    )}
                     style={{ width: `${DAY_WIDTH}px`, left: `${index * DAY_WIDTH}px`, zIndex: (labelMode === 'month' ? isMonthStart(date) : labelMode === 'week' ? date.getDay() === 1 : true) ? 10 : 1 }}
                    >
                      <div
                        className={`transform -rotate-90 ${isMobile ? 'text-[8px]' : 'text-[10px]'} whitespace-nowrap relative z-10 pointer-events-none`}
                        style={zoomActive && labelMode !== 'day' ? { transform: 'rotate(-90deg) translateY(6px)' } : undefined}
                      >

                        {zoomActive
                          ? (labelMode === 'week'
                              ? (date.getDay() !== 1
                                  ? ''
                                  : (date.getDate() <= 7
                                      ? `${String(date.getDate()).padStart(2,'0')}/${String(date.getMonth()+1).padStart(2,'0')}`
                                      : String(date.getDate()).padStart(2,'0')))
                              : (labelMode === 'month'
                                  ? (date.getDate() !== 1
                                      ? ''
                                      : `${String(date.getMonth()+1).padStart(2,'0')}/${date.getFullYear().toString().slice(-2)}`)
                                  : date.getDate()))
                          : (isMonthStart(date)
                              ? (() => {
                                  const day = String(date.getDate()).padStart(2, '0');
                                  const month = String(date.getMonth() + 1).padStart(2, '0');
                                  const year = date.getFullYear().toString().slice(-2);
                                  return `${day}-${month}-${year}`;
                                })()
                              : date.getDate()
                            )
                        }
                      </div>
                   </div>
                ))}
              </div>
            </div>
          </div>

          {/* Body scroller - horizontal scroll only */}
          <div
            ref={setGanttNode}
            className="overflow-x-auto touch-pan-x"
            onScroll={handleScroll}
            style={{
              WebkitOverflowScrolling: 'touch',
              scrollbarWidth: isMobile ? 'thin' : 'auto'
            }}
          >
            <div 
              ref={containerRef}
              className="min-w-full relative" 
              style={{ 
                width: `${dateColumns.length * DAY_WIDTH}px`,
                minWidth: '100%',
                backgroundImage: 'repeating-linear-gradient(to right, transparent, transparent 31px, var(--border) 31px, var(--border) 32px)',
                backgroundSize: '32px 100%',
                backgroundRepeat: 'repeat-x'
              }}
            >
            {/* Timeline Body */}
            <div className="flex flex-col">
              {items.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">
                  {/* Empty state - no translation needed, users understand from context */}
                </div>
                ) : (
                (groupedRows ?? [{ key: '__all__', label: '', items, start: null, end: null }]).map((g) => (
                  <React.Fragment key={g.key}>
                    {groupedRows && (
                      <div className="border-b relative" style={{ height: isMobile ? '35px' : '40px' }}>
                        <div className="relative w-full h-full min-w-full"
                             style={{ width: `max(${dateColumns.length * DAY_WIDTH}px, 100%)` }}>
                          {/* Timeline grid background */}
                          {dateColumns.map((date, index) => (
                            <div
                              key={index}
                              className={cn(
                                "absolute top-0 h-full",
                                DAY_WIDTH >= 4 && "border-r border-border/50",
                                isWeekend(date) && "bg-muted/30",
                                isMonthStart(date) && (
                                  zoomActive && labelMode !== 'day'
                                    ? "border-l border-primary/40"
                                    : "border-l-2 border-primary"
                                ),
                                isToday(date) && "bg-primary/10"
                              )}
                              style={{ 
                                left: `${index * DAY_WIDTH}px`,
                                width: `${DAY_WIDTH}px`,
                                zIndex: 2
                              }}
                            />
                          ))}
                          {g.start && g.end && (() => {
                            const sKey = toDateKey(g.start), eKey = toDateKey(g.end);
                            const vS = ymd(dateColumns[0]), vE = ymd(dateColumns[dateColumns.length - 1]);
                            if (eKey < vS || sKey > vE) return null;
                            const visS = sKey < vS ? vS : sKey;
                            const visE = eKey > vE ? vE : eKey;
                            const daysBetween = (a: string, b: string) =>
                              Math.max(0, Math.floor((new Date(b+'T00:00:00').getTime() - new Date(a+'T00:00:00').getTime()) / 86400000));
                            const left = daysBetween(vS, visS) * DAY_WIDTH;
                            const w = calculateDuration(visS, visE) * DAY_WIDTH;
                            return (
                              <div className="absolute top-1/2 -translate-y-1/2 pointer-events-none"
                                   style={{ left: `${left}px`, width: `${w}px`, height: '10px', zIndex: 20 }}>
                                 <div className="absolute inset-0 rounded-sm"
                                      style={{
                                        backgroundImage: 'repeating-linear-gradient(45deg, #64748B 0 1px, transparent 1px 5px)',
                                        opacity: 0.45,
                                      }} />
                                 <div className="absolute left-0 top-0 h-full"
                                      style={{ width: `${g.pct}%`, backgroundColor: '#64748B', opacity: 0.3 }} />
                                 <div className="absolute left-0 top-0 h-[2px]" style={{ width: '100%', backgroundColor: '#94A3B8' }} />
                                 <div className="absolute left-0 top-0 h-full w-[2px]" style={{ backgroundColor: '#94A3B8' }} />
                                 <div className="absolute right-0 top-0 h-full w-[2px]" style={{ backgroundColor: '#94A3B8' }} />
                                 <div className="absolute left-0 bottom-0 w-full h-[2px]" style={{ backgroundColor: '#94A3B8' }} />
                               </div>
                            );
                          })()}
                        </div>
                      </div>
                    )}
                    {(collapsedGroups.has(g.key) ? [] : g.items).map((item, itemIndex) => {
                  const allMilestones = milestonesByItem[item.id] || [];
                  const itemMilestones = filterMilestones(allMilestones, filters.activeMilestoneTypes, filters.milestoneName, milestoneFilterRange);
                  const isExpanded = expandedItems.has(item.id);
                  const hasHighlight = item.hasHighlightedMilestone;
                  const isStartSelected = summaryMilestoneIds?.has(`item-start-${item.id}`);
                  const isEndSelected = summaryMilestoneIds?.has(`item-end-${item.id}`);
                  
                  return (
                    <React.Fragment key={item.id}>
                      {/* Main Item Row */}
                      <div 
                        ref={setRowRef(`item-${item.id}`)}
                        className={cn(
                          "border-b hover:bg-muted/50 relative",
                          hasHighlight && "bg-primary/5"
                        )} 
                        style={{ height: isMobile ? '35px' : '40px' }}
                      >
                        <div 
                          className="relative w-full h-full min-w-full"
                          style={{ 
                            width: `max(${dateColumns.length * DAY_WIDTH}px, 100%)`
                          }}
                        >
                          {/* Item Progress Bar - Viewport-clamped with timezone-aware dates */}
                          {(() => {
                            const completion = item.completion_percentage ?? 0;
                            
                            // Use timezone-aware date keys
                            const itemStartKey = toDateKey(item.startDate);
                            const itemEndKey = toDateKey(item.endDate);
                            const viewportStartKey = ymd(dateColumns[0]);
                            const viewportEndKey = ymd(dateColumns[dateColumns.length - 1]);
                            
                            // Validation: check for invalid date ranges
                            if (itemEndKey < itemStartKey) {
                              logger.error('ItemsTimeline', 'Invalid date range', {
                                item: item.name,
                                start: item.startDate,
                                end: item.endDate
                              });
                              return null;
                            }
                            
                            // Check if item is outside viewport
                            if (itemEndKey < viewportStartKey || itemStartKey > viewportEndKey) {
                              return null; // Don't render bar outside viewport
                            }
                            
                            // Clamp visible range to viewport
                            const visibleStartKey = itemStartKey < viewportStartKey ? viewportStartKey : itemStartKey;
                            const visibleEndKey = itemEndKey > viewportEndKey ? viewportEndKey : itemEndKey;
                            
                            // Helper: calculate days between two date keys (non-inclusive)
                            const daysBetween = (fromKey: string, toKey: string): number => {
                              const from = new Date(fromKey + 'T00:00:00');
                              const to = new Date(toKey + 'T00:00:00');
                              return Math.max(0, Math.floor((to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24)));
                            };
                            
                            // Calculate bar position and width
                            const leftDays = daysBetween(viewportStartKey, visibleStartKey);
                            const widthDays = calculateDuration(visibleStartKey, visibleEndKey); // Inclusive
                            
                            const barStart = leftDays * DAY_WIDTH;
                            const barWidth = widthDays * DAY_WIDTH;
                            
                            // Debug logging (dev only)
                            if (import.meta.env.DEV && (itemStartKey < viewportStartKey || itemEndKey > viewportEndKey)) {
                              logger.debug('ItemsTimeline', 'Bar clamp applied', {
                                item: item.name,
                                itemStartKey,
                                itemEndKey,
                                viewportStartKey,
                                viewportEndKey,
                                visibleStartKey,
                                visibleEndKey,
                                leftDays,
                                widthDays
                              });
                            }
                            
                            // Fixed colors for all items - grayscale balanced light
                            const fillColor = '#9CA3AF'; // Filled portion (medium-light gray) - gray-400
                            const trackBg = '#D1D5DB'; // Unfilled portion (light gray) - gray-300
                            const trackBorder = '#D1D5DB'; // Border same as track
                            
                            return (
                              <div
                                className="absolute top-1/2 -translate-y-1/2 rounded-sm border overflow-hidden"
                                style={{
                                  left: `${barStart}px`,
                                  width: `${barWidth}px`,
                                  height: '8px',
                                  backgroundColor: trackBg,
                                  borderColor: trackBorder,
                                  zIndex: 20
                                }}
                              >
                                {/* Progress fill INSIDE container */}
                                <div
                                  className="absolute inset-y-0 left-0 rounded-sm transition-all"
                                  style={{
                                    width: `${completion}%`,
                                    backgroundColor: fillColor,
                                    borderRight: `1px solid ${fillColor}`
                                  }}
                                />
                              </div>
                            );
                          })()}
                          
                          {/* Timeline grid background */}
                          {dateColumns.map((date, index) => (
                            <div
                              key={index}
                              className={cn(
                                "absolute top-0 h-full",
                                DAY_WIDTH >= 4 && "border-r border-border/50",
                                isWeekend(date) && "bg-muted/30",
                                isMonthStart(date) && (
                                  zoomActive && labelMode !== 'day'
                                    ? "border-l border-primary/40"
                                    : "border-l-2 border-primary"
                                ),

                                isToday(date) && "bg-primary/10"
                              )}
                              style={{ 
                                left: `${index * DAY_WIDTH}px`,
                                width: `${DAY_WIDTH}px`,
                                zIndex: 2
                              }}
                            />
                          ))}
                          
                          {/* Timeline Bar - render on top of grid */}
                          <TimelineBar
                            startDate={item.startDate}
                            endDate={item.endDate}
                            dateColumns={dateColumns}
                            dayWidth={DAY_WIDTH}
                            category={item.category}
                            className="z-10"
                            itemId={item.id}
                            projectId={item.projectId}
                            sortOrder={item.sortOrder}
                            canDrag={viewOnly ? false : (readOnly ? false : canEditBasic)}
                            highlighted={isStartSelected || isEndSelected}
                            onCommitDates={handleCommitDates}
                            onRequestDependency={viewOnly ? undefined : (readOnly ? undefined : openItemDependency)}
                            onCreateParallelItem={viewOnly ? undefined : onCreateParallelItem}
                            defaultMilestoneType={keyGroupForItem(item)?.milestoneType}
                          />
                          
                          {/* Milestone Markers (when collapsed) */}
                          {milestoneFeatureEnabled && !isExpanded && itemMilestones.filter(m => m.id).map((milestone) => (
                            <MilestoneErrorBoundary
                              key={milestone.id!}
                              itemId={item.id}
                              milestoneName={milestone.name}
                            >
                              <MilestoneMarker
                                milestone={milestone as any}
                                dateColumns={dateColumns}
                                dayWidth={DAY_WIDTH}
                                staticMilestoneTypes={staticMilestoneTypes}
                                highlighted={(summaryMilestoneIds?.has(`milestone-start-${milestone.id}`) || summaryMilestoneIds?.has(`milestone-end-${milestone.id}`) || summaryMilestoneIds?.has(milestone.id!)) ?? false}
                                onRequestDependency={viewOnly ? undefined : (readOnly ? undefined : openMilestoneDependency)}
                              />

                            </MilestoneErrorBoundary>
                          ))}
                        </div>
                      </div>
                      
                      {/* Expanded Milestone Rows */}
                      {!isReorderMode && milestoneFeatureEnabled && isExpanded && itemMilestones.filter(m => m.id).map((milestone) => {
                        
                        return (
                          <div
                            key={milestone.id!}
                            ref={setRowRef(`ms-${milestone.id!}`)}
                            className="border-b relative"
                            style={{ height: `${MILESTONE_ROW_HEIGHT}px` }}
                          >
                            <div 
                              className="relative w-full h-full min-w-full"
                              style={{ 
                                width: `max(${dateColumns.length * DAY_WIDTH}px, 100%)`
                              }}
                            >
                              {/* Timeline grid background */}
                              {dateColumns.map((date, index) => (
                                <div
                                  key={index}
                                  className={cn(
                                    "absolute top-0 h-full",
                                    DAY_WIDTH >= 4 && "border-r border-border/50",
                                    isWeekend(date) && "bg-muted/30",
                                    isMonthStart(date) && (
                                      zoomActive && labelMode !== 'day'
                                        ? "border-l border-primary/40"
                                        : "border-l-2 border-primary"
                                    ),

                                    isToday(date) && "bg-primary/10"
                                  )}
                                  style={{ 
                                    left: `${index * DAY_WIDTH}px`,
                                    width: `${DAY_WIDTH}px`
                                  }}
                                />
                              ))}
                              
                              {/* Milestone Bar */}
                              <MilestoneErrorBoundary
                                itemId={item.id}
                                milestoneName={milestone.name}
                              >
                                <MilestoneBar
                                  milestone={milestone as any}
                                  dateColumns={dateColumns}
                                  dayWidth={DAY_WIDTH}
                                  height={MILESTONE_BAR_HEIGHT}
                                  canDrag={viewOnly ? false : (readOnly ? false : canEditMilestone)}
                                  highlighted={(summaryMilestoneIds?.has(`milestone-start-${milestone.id}`) || summaryMilestoneIds?.has(`milestone-end-${milestone.id}`) || summaryMilestoneIds?.has(milestone.id)) ?? false}
                                  onCommitDates={handleCommitMilestoneDates}
                                  onRequestDependency={viewOnly ? undefined : (readOnly ? undefined : openMilestoneDependency)}
                                  itemStartDate={item.startDate}
                                  itemEndDate={item.endDate}
                                />
                              </MilestoneErrorBoundary>
                            </div>
                          </div>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
                  </React.Fragment>
                )))}
            </div>
            {/* ✅ Overlay connector: dependency edges (predecessor.end → successor.start) */}
            {edgePoints.length >= 1 && (
              <svg
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: `${dateColumns.length * DAY_WIDTH}px`,
                  height: '100%',
                  pointerEvents: 'none',
                  zIndex: 31,
                }}
              >
                {(() => {
                  // Stagger labels per row (same targetY) to avoid overlap
                  const orderIdx = new Map<string, number>();
                  const groups = new Map<number, typeof edgePoints>();
                  for (const ep of edgePoints) {
                    const rowKey = Math.round(ep.p2.y);
                    const arr = groups.get(rowKey) || [];
                    arr.push(ep);
                    groups.set(rowKey, arr);
                  }
                  for (const arr of groups.values()) {
                    arr.sort((a, b) => a.p2.x - b.p2.x);
                    arr.forEach((ep, i) => orderIdx.set(ep.key, i));
                  }
                  return edgePoints.map((ep) => {
                  const { p1, p2, type, lag, actualX } = ep;
                  let d: string;
                  if (p1.x === p2.x || p1.y === p2.y) {
                    d = `M ${p1.x} ${p1.y} L ${p2.x} ${p2.y}`;
                  } else {
                    d = `M ${p1.x} ${p1.y} L ${p2.x} ${p1.y} L ${p2.x} ${p2.y}`;
                  }
                  if (actualX != null && actualX !== p2.x) {
                    d += ` L ${actualX} ${p2.y}`;
                  }
                  const label = lag !== 0
                    ? `${type}${lag > 0 ? '+' : ''}${lag}d`
                    : type;
                  const labelX = p2.x + (p2.x >= p1.x ? 4 : -4);
                  const idx = orderIdx.get(ep.key) ?? 0;
                  const labelY = p2.y - 4 - idx * 11;
                  return (
                    <g key={ep.key}>
                      <path
                        d={d}
                        fill="none"
                        stroke="#94a3b8"
                        strokeWidth={1.5}
                        strokeDasharray="4 3"
                      />
                      <circle cx={p1.x} cy={p1.y} r={2.5} fill="#94a3b8" />
                      <circle cx={p2.x} cy={p2.y} r={2.5} fill="#94a3b8" />
                      {actualX != null && actualX !== p2.x && (
                        <circle cx={actualX} cy={p2.y} r={2} fill="#94a3b8" />
                      )}
                      <text
                        x={labelX}
                        y={labelY}
                        fontSize={9}
                        fill="#94a3b8"
                        textAnchor={p2.x >= p1.x ? 'start' : 'end'}
                        style={{ userSelect: 'none' }}
                      >
                        {label}
                      </text>
                    </g>
                  );
                });
                })()}
              </svg>
            )}
            </div>

          </div>
        </div>
      </div>

      {!readOnly && (
        <ConfirmDialog
          isOpen={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          onConfirm={handleDeleteConfirm}
          title="Xác nhận xóa Item"
          description={`Bạn có chắc chắn muốn xóa item "${itemToDelete?.name}"? Hành động này không thể hoàn tác.`}
          confirmText="Xóa"
          cancelText="Hủy"
        />
      )}
      {!readOnly && (
        <DependencyDialog
          open={depDialog.open}
          onClose={() => setDepDialog(d => ({ ...d, open: false }))}
          sourceKind={depDialog.kind}
          sourceId={depDialog.id}
          sourceName={depDialog.name}
          availableItems={items}
          milestones={flatMilestones}
        />
      )}
    </div>
    </ErrorBoundary>
  );
};

export default ItemsTimeline;